import React, { useState, useEffect } from 'react';
import {
  FaSpinner,
  FaMicrophone,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaTimes,
  FaEye,
  FaRedo,
  FaSkullCrossbones,
  FaHistory,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from 'react-router-dom';
import DescriptionViewer from "../../../Common/DescriptionViewer";
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const RejectedFreeConferance = () => {
  const navigate = useNavigate();
  const [rejectedConferences, setRejectedConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const stats = {
    rejectedConferences: rejectedConferences.length,
  };

  const fetchRejectedConferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/rejected/conferences`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const conferences = response.data?.data || [];
      setRejectedConferences(conferences);
      setError(null);
    } catch (err) {
      console.error("Error fetching rejected conferences:", err);
      setError("Failed to load rejected conferences.");
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRejectedConferences();
  }, []);

  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedConference(null);
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-rose-500 to-red-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Declined Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Free Coverage</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-rose-600 italic leading-none">{stats.rejectedConferences} Records</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchRejectedConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-rose-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="free" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {/* Statistics - Ultra Compact */}
        {!loading && !error && (
          <div className="grid grid-cols-1 gap-2">
            <div className="bg-slate-900 rounded-2xl p-4 text-white relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-white/5 rounded-full blur-xl group-hover:bg-white/10 transition-all" />
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1 tracking-widest">Total Declined Records</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-rose-400">{stats.rejectedConferences}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
              <FaSkullCrossbones className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-rose-500 text-[8px]" />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Scouring Dead Ends...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Mission Code</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Deployment</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Declined Reason</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Terminated At</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Intel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rejectedConferences.map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center border border-rose-100/50">
                          <FaSkullCrossbones className="text-rose-600 text-[10px]" />
                        </div>
                        <span className="font-black text-slate-400 text-[10px] tabular-nums tracking-tighter italic truncate max-w-[120px]">#{conference.conferenceId}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-slate-700 italic truncate max-w-[150px]">{conference.conferenceDetails?.topic || "N/A"}</p>
                      <p className="text-[8px] font-bold text-slate-400 uppercase leading-none mt-0.5">{conference.conferenceDetails?.conferenceDate}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-rose-600 line-clamp-1 italic max-w-[200px] leading-tight">"{conference.rejectNote || "No reason provided"}"</p>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums leading-none">
                      {conference.rejectedAt ? new Date(conference.rejectedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => handleViewDetails(conference)} className="h-8 px-4 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 mx-auto"><FaEye /> View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {rejectedConferences.length > 0 ? (
              rejectedConferences.map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-rose-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-rose-500 to-red-600 p-0.5 shadow-md shadow-rose-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaTimes className="text-rose-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.conferenceDetails?.topic || "N/A"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest tabular-nums italic">Code #{conference.conferenceId}</p>
                          <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-rose-100 text-rose-800">Declined</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Reason Box */}
                    <div className="bg-white rounded-xl p-3 border border-rose-100/50">
                      <p className="text-[8px] font-black text-rose-300 uppercase leading-none mb-1.5 flex items-center gap-1"><FaSkullCrossbones className="text-[7px]" /> Primary Reason</p>
                      <p className="text-[10px] font-black text-rose-700 leading-relaxed italic line-clamp-2">" {conference.rejectNote || "No specific reason logged in the database."} "</p>
                    </div>

                    {/* Single Action */}
                    <button onClick={() => handleViewDetails(conference)} className="w-full h-11 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest"><FaEye /> Re-inspect Case</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaHistory className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">Dead End Empty</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No rejected coverage records found in our terminated logs.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Official Rejection Record Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-rose-600 to-red-700 p-6 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-black tracking-tighter leading-tight mb-1">Terminated Mission Logs</h3>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-rose-100 italic">Declined coverage report & reason</p>
                </div>
                <button onClick={handleCloseModal} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-all"><FaTimes className="text-xs" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Rejection Header */}
              <div className="bg-rose-50/50 rounded-2xl p-4 border border-rose-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FaSkullCrossbones className="text-rose-600" />
                  <span className="text-[10px] font-black text-rose-800 uppercase tracking-widest">Acknowledge: Case Terminated</span>
                </div>
              </div>

              {/* Basic Intel */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Subject</p>
                  <p className="text-[11px] font-black text-slate-800">{selectedConference.conferenceDetails?.topic || "N/A"}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Target Date</p>
                  <p className="text-[11px] font-black text-slate-800 tabular-nums italic">{selectedConference.conferenceDetails?.conferenceDate}</p>
                </div>
              </div>

              {/* Detailed Location Footprint */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 space-y-3">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Target Location Intel</p>
                <div className="flex items-center gap-3 text-[10px] font-black text-slate-800">
                  <FaMapMarkerAlt className="text-rose-500" />
                  <span className="italic uppercase">{selectedConference.conferenceDetails?.city}, {selectedConference.conferenceDetails?.state}</span>
                </div>
                <p className="text-[9px] font-bold text-slate-500 uppercase leading-relaxed italic">{selectedConference.conferenceDetails?.place} ({selectedConference.conferenceDetails?.landmark})</p>
              </div>

              {/* Rejection Log */}
              <div className="bg-rose-50/30 rounded-2xl p-4 border border-rose-100/50 space-y-2">
                <p className="text-[8px] font-black text-rose-400 uppercase tracking-widest">Termination Footnote</p>
                <p className="text-[11px] font-black text-rose-800 leading-relaxed italic">" {selectedConference.rejectNote || "No specific termination reason was provided at the time of bypass."} "</p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 flex items-center justify-center px-6 border-t border-slate-100/50">
              <button onClick={handleCloseModal} className="h-10 w-full bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-slate-200">Seal Archive</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default RejectedFreeConferance;